// ===========================================================================
// CostBreakdownPanel — drawer lateral con el desglose de costo de un paciente.
// Se abre desde el botón "View cost" de la lista. Datos: getPatientDetail.
// ===========================================================================
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Stack,
  Divider,
  LinearProgress,
  CircularProgress,
  Button,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { getPatientDetail } from "../../services";
import { COST_CATEGORIES } from "../../common/constants";
import { formatCurrency, formatCurrencyCents } from "../../utils/format";
import { HIGH_COST_BASE_PATH } from "../../common/constants";

export default function CostBreakdownPanel({ cipId, open, onClose }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [header, setHeader] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !cipId) return;
    let active = true;
    setLoading(true);
    setError(null);
    setHeader(null);
    (async () => {
      try {
        const detail = await getPatientDetail(cipId);
        if (active) setHeader(detail?.header || null);
      } catch (err) {
        if (active) setError(err?.response?.status === 404 ? "No High Cost record for this patient." : "Could not load cost detail.");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [open, cipId]);

  const total = Number(header?.totalCost) || 0;

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: 420, maxWidth: "100%" } }}>
      <Box sx={{ p: 2.5 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Cost breakdown
          </Typography>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>

        {loading && <CircularProgress size={24} sx={{ mt: 2 }} />}
        {error && <Alert severity="warning" sx={{ mt: 1 }}>{error}</Alert>}

        {header && (
          <>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {header.memberFullName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {header.cipId} · {header.age}y · {header.gender} · {header.pcpName || "Unassigned"}
            </Typography>

            <Box sx={{ my: 2, p: 1.5, borderRadius: 2, bgcolor: "action.hover" }}>
              <Typography variant="caption" color="text.secondary">
                Total cost
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>
                {formatCurrency(total)}
              </Typography>
            </Box>

            <Divider sx={{ mb: 1.5 }} />

            <Stack spacing={1.5}>
              {COST_CATEGORIES.map((cat) => {
                const value = Number(header[cat.key]) || 0;
                const pct = total > 0 ? (value / total) * 100 : 0;
                return (
                  <Box key={cat.key}>
                    <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.3 }}>
                      <Typography variant="body2">{cat.label}</Typography>
                      <Typography variant="body2" sx={{ fontVariantNumeric: "tabular-nums" }}>
                        {formatCurrencyCents(value)}
                      </Typography>
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={pct}
                      sx={{
                        height: 6,
                        borderRadius: 3,
                        bgcolor: "action.hover",
                        "& .MuiLinearProgress-bar": { bgcolor: cat.color },
                      }}
                    />
                  </Box>
                );
              })}
            </Stack>

            <Button
              fullWidth
              variant="outlined"
              endIcon={<OpenInNewIcon />}
              onClick={() => navigate(`${HIGH_COST_BASE_PATH}/${encodeURIComponent(header.cipId)}`)}
              sx={{ mt: 3, textTransform: "none" }}
            >
              Open full patient record
            </Button>
          </>
        )}
      </Box>
    </Drawer>
  );
}
